'use client';

import { type RefObject, useEffect, useState } from 'react';

/**
 * True once `ref` has come within `rootMargin` of the viewport, and true from
 * then on.
 *
 * Written for `<video>`: a mounted video is a video the browser decodes, and a
 * decoded 1080p frame costs roughly 8MB of GPU texture whether or not it is on
 * screen. `preload="metadata"` does not save you when something then seeks the
 * element — a `#t=` fragment or a `currentTime` assignment both force the
 * decode that the attribute was meant to avoid.
 *
 * Defaults to a generous margin so the media is ready well before it is
 * reached; the point is to avoid decoding what a visitor never scrolls to, not
 * to decode at the last moment.
 */
export function useNearViewport<T extends Element>(
  ref: RefObject<T | null>,
  rootMargin = '200% 0px',
): boolean {
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el) return;

    // No observer (older browsers, jsdom): load rather than never show.
    // Queued rather than set inline — a synchronous setState in an effect body
    // cascades an extra render, and the server renders with no observer too.
    if (typeof IntersectionObserver === 'undefined') {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) setNear(true);
      });
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin, near]);

  return near;
}

export default useNearViewport;
